import * as React from 'react'
import { TerminalNodeInterpreter } from '../model/TerminalNodeInterpreter';
import { IStableTerminalNode } from '../model/TerminalScore';
import { TerminalScoreContext } from '../TerminalScoreProvider';
import humanizeDuration from "humanize-duration";
import { BtcInfoContext } from '../../general/SatoshiPriceProvider';
import NumberFormat from 'react-number-format';
import TerminalStablePeersRow from './TerminalStablePeerRow';
import { TerminalScoreManager } from '../model/TerminalScoreManager';
import { GDataGraph } from '../../graph/GDataGraph';
import { GraphEdge } from '../../graph/models/GraphEdge.interface';
import { Pagination } from '@mui/material';


type Props = {
  nodeId: string,
  className?: string
}

function getStableEdges(nodeId: string, currentBlockHeight: number, terminalScore: TerminalScoreManager, graph: GDataGraph): Map<string, GraphEdge[]> {
  const terminalNode = terminalScore.getNode(nodeId);
  if (!terminalNode) {
    return new Map();
  }
  const interpreter = new TerminalNodeInterpreter(terminalNode);
  const edges = graph.getIncomingEdges(nodeId)
    .sort((a, b) => {
      const aBlockCount = Number.parseInt(a.id.split('x')[0]);
      const bBlockCount = Number.parseInt(b.id.split('x')[0]);
      return bBlockCount - aBlockCount;
    })
    .sort((a, b) => {
      return b.capacitySat - a.capacitySat;
    })
  const goodPeers = Array.from(new Set<string>([
    ...terminalScore.scoredNodeIds,
    ...terminalScore.stableNodeIds
  ]));

  const myGoodEdges = edges.filter(edge => goodPeers.includes(edge.source));

  myGoodEdges.sort((a, b) => {
    const aIsStable = interpreter.stablePeers.includes(a.source);
    const bIsStable = interpreter.stablePeers.includes(b.source);
    return (aIsStable === bIsStable) ? 0 : aIsStable ? -1 : 1;
  })


  const groupedByPeer = new Map<string, GraphEdge[]>();

  for (const edge of myGoodEdges) {
    const peerId = edge.source;
    if (!groupedByPeer.has(peerId)) {
      groupedByPeer.set(peerId, []);
    }
    groupedByPeer.get(peerId).push(edge);
  }
  return groupedByPeer;
}

const TerminalStablePeers: React.FC<Props> = ({ nodeId, className = '' }) => {
  const { graph, terminalScore } = React.useContext(TerminalScoreContext);
  const { currentBlockHeight } = React.useContext(BtcInfoContext);
  const [page, setPage] = React.useState<number>(1);
  const [allEdges, setAllEdges] = React.useState<GraphEdge[][]>([]);
  const [pageEdges, setPageEdges] = React.useState<GraphEdge[][]>([]);
  const rowsPerPage = 10;

  React.useEffect(() => {
    const myGoodEdges = getStableEdges(nodeId, currentBlockHeight, terminalScore, graph);
    setAllEdges(Array.from(myGoodEdges.values()));
    setPage(1);
  }, [graph, terminalScore, currentBlockHeight, nodeId])

  React.useEffect(() => {
    const pageEdge = allEdges.sort((aEdges, bEdges) => {
      const aCentrality = aEdges.map(edge => edge.centrality).reduce((a,b) => a+b, 0);
      const bCentrality = bEdges.map(edge => edge.centrality).reduce((a,b) => a+b, 0);

      return bCentrality - aCentrality;
    }).slice((page - 1) * rowsPerPage, page * rowsPerPage);
    setPageEdges(pageEdge);
  }, [allEdges, page]);



  const headerClasses = 'pl-2 text-left pr-4 text-gray-700 font-normal text-xs uppercase py-2 pl-2 border-b bg-gray-100'
  return (
    <div className={' ' + className}>
      <table className="w-full bg-white">
        <thead>
          <tr >
            <th className={headerClasses + ' rounded-tl'}>Peers</th>
            <th className={headerClasses + ' rounded-tr'}>Ack by Terminal</th>
          </tr>
        </thead>
        <tbody>
          {allEdges.length > 0 &&
            pageEdges.map(edges => <TerminalStablePeersRow edges={edges} key={'stable-inbound-nodes-' + edges[0].source} />)
          }

        </tbody>
      </table>
      {allEdges.length === 0 &&
        <div className="pl-2 py-2 border-b">
          The node doesn't have a channel with a stable node or the channel does not fullfill the <a href="#good-channel" className="underline">good channel</a> heuristics.
        </div>
      }
      <div className="mt-8 flex justify-center">
        <Pagination color="primary" page={page} onChange={(e, pageNumber) => setPage(pageNumber)} count={Math.ceil(allEdges.length / rowsPerPage)} shape="rounded" />
      </div>
    </div>
  );
}

export default TerminalStablePeers;

